import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { classActions, studentActions } from '../../redux/actions';
import { attendanceSessionService } from '../../services/attendanceSession.service';

// ── SOUND HELPERS ────────────────────────────────────────────────────────────
// Dùng singleton AudioContext — tránh lỗi browser giới hạn số context
let _audioCtx: AudioContext | null = null;
function getCtx(): AudioContext {
  if (!_audioCtx || _audioCtx.state === 'closed') {
    _audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (_audioCtx.state === 'suspended') _audioCtx.resume();
  return _audioCtx;
}

function playPick() {
  try {
    const ctx = getCtx(); const t = ctx.currentTime;
    [523, 659, 784, 1047].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = f; o.type = 'sine';
      g.gain.setValueAtTime(0.3, t + i * 0.08);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.08 + 0.15);
      o.start(t + i * 0.08); o.stop(t + i * 0.08 + 0.2);
    });
  } catch {}
}
function playTick() {
  try {
    const ctx = getCtx();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 800; o.type = 'square';
    g.gain.setValueAtTime(0.08, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    o.start(); o.stop(ctx.currentTime + 0.06);
  } catch {}
}
function playWhoosh() {
  try {
    const ctx = getCtx();
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.3, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource(); src.buffer = buf;
    const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 1200;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.3, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    src.connect(f); f.connect(g); g.connect(ctx.destination);
    src.start(); src.stop(ctx.currentTime + 0.3);
  } catch {}
}


// ── ANIMATIONS ───────────────────────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.06)}`;
const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const bounce = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-16px)}`;
const glow = keyframes`0%,100%{box-shadow:0 0 20px rgba(167,139,250,0.4)}50%{box-shadow:0 0 40px rgba(167,139,250,0.9),0 0 60px rgba(96,165,250,0.5)}`;
const cardFlip = keyframes`from{transform:rotateY(180deg)}to{transform:rotateY(0deg)}`;
const rainbowBg = keyframes`0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}`;
const confettiFall = keyframes`0%{transform:translateY(-20px) rotate(0deg);opacity:1}100%{transform:translateY(100vh) rotate(720deg);opacity:0}`;

// ── STYLED COMPONENTS ─────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  color: white;
  position: relative;
  overflow: hidden;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: 900;
  background: linear-gradient(90deg, #a78bfa, #60a5fa, #34d399);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 24px;
`;

const SetupRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  align-items: flex-end;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const SelectBox = styled.select`
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.08);
  color: white;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  min-width: 180px;
  cursor: pointer;
  option { background: #1e293b; color: white; }
`;

const Label = styled.div`font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 6px; letter-spacing: 1px; text-transform: uppercase;`;

const ModeBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 32px;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 20px;
  border: 2px solid ${p => p.$active ? '#a78bfa' : 'rgba(255,255,255,0.15)'};
  background: ${p => p.$active ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : 'transparent'};
  color: white;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #a78bfa; transform: translateY(-2px); }
`;

const ArenaBox = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const BigPickBtn = styled.button<{ $spinning?: boolean }>`
  display: block;
  width: 100%;
  padding: 20px;
  border-radius: 18px;
  border: none;
  background: ${p => p.$spinning
    ? 'linear-gradient(270deg,#7c3aed,#2563eb,#059669,#d97706,#7c3aed);background-size:400% 400%'
    : 'linear-gradient(135deg,#7c3aed,#4f46e5)'};
  color: white;
  font-size: 20px;
  font-weight: 900;
  cursor: ${p => p.$spinning ? 'not-allowed' : 'pointer'};
  margin-bottom: 24px;
  transition: all 0.25s;
  ${p => p.$spinning && css`animation: ${rainbowBg} 1s linear infinite;`}
  &:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(124,58,237,0.5); }
`;

// ── AVATAR DISPLAY ────────────────────────────────────────────────────────────
const AvatarCircle = styled.div<{ $size?: number; $color: string; $src?: string; $bounce?: boolean; $glow?: boolean }>`
  width: ${p => p.$size || 64}px;
  height: ${p => p.$size || 64}px;
  border-radius: 50%;
  background: ${p => p.$src ? 'transparent' : p.$color};
  border: 3px solid rgba(255,255,255,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${p => (p.$size || 64) * 0.38}px;
  font-weight: 900;
  color: white;
  overflow: hidden;
  flex-shrink: 0;
  ${p => p.$bounce && css`animation: ${bounce} 0.6s ease infinite;`}
  ${p => p.$glow && css`animation: ${glow} 1.5s ease-in-out infinite;`}
  transition: transform 0.2s;
`;

const AvatarImg = styled.img`width:100%;height:100%;object-fit:cover;border-radius:50%;`;

// ── GRID OF STUDENTS ──────────────────────────────────────────────────────────
const StudentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const StudentCard = styled.div<{ $picked: boolean; $highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 8px;
  border-radius: 16px;
  background: ${p => p.$picked ? 'rgba(255,255,255,0.04)' : p.$highlighted ? 'rgba(167,139,250,0.2)' : 'rgba(255,255,255,0.07)'};
  border: 2px solid ${p => p.$picked ? 'rgba(255,255,255,0.06)' : p.$highlighted ? '#a78bfa' : 'rgba(255,255,255,0.1)'};
  opacity: ${p => p.$picked ? 0.35 : 1};
  transition: all 0.3s;
  cursor: ${p => p.$picked ? 'default' : 'pointer'};
  position: relative;
  ${p => p.$highlighted && css`animation: ${glow} 1.2s ease-in-out infinite;`}
  &:hover:not([data-picked="true"]) { transform: translateY(-3px); }
`;

const StudentCardName = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  color: #e2e8f0;
`;

const PickedBadge = styled.div`
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 14px;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(239,68,68,0.85);
  color: white;
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: all 0.15s;
  z-index: 10;
  &:hover { background: #ef4444; transform: scale(1.2); }
`;

const ExcludedCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 8px;
  border-radius: 16px;
  background: rgba(239,68,68,0.06);
  border: 2px dashed rgba(239,68,68,0.25);
  opacity: 0.45;
  position: relative;
  cursor: default;
`;

const EditModeBtn = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 20px;
  border: 2px solid ${p => p.$active ? '#f97316' : 'rgba(255,255,255,0.15)'};
  background: ${p => p.$active ? 'rgba(249,115,22,0.2)' : 'transparent'};
  color: ${p => p.$active ? '#fb923c' : '#94a3b8'};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #f97316; color: #fb923c; }
`;

// ── WINNER DISPLAY ────────────────────────────────────────────────────────────
const WinnerCard = styled.div`
  background: linear-gradient(135deg, rgba(124,58,237,0.3), rgba(79,70,229,0.3));
  border: 2px solid rgba(167,139,250,0.6);
  border-radius: 24px;
  padding: 32px;
  text-align: center;
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease;
  ${css`animation: ${glow} 2s ease-in-out infinite;`}
`;

const WinnerLabel = styled.div`font-size: 14px; color: #a78bfa; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 12px;`;
const WinnerName = styled.div`font-size: 36px; font-weight: 900; margin-top: 12px; color: white;`;

// ── PICKED HISTORY ─────────────────────────────────────────────────────────
const HistorySection = styled.div`
  max-width: 900px;
  margin: 0 auto 24px;
  background: rgba(255,255,255,0.04);
  border-radius: 16px;
  padding: 16px 20px;
  border: 1px solid rgba(255,255,255,0.08);
`;
const HistoryTitle = styled.div`font-size: 12px; font-weight: 700; color: #64748b; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 10px;`;
const HistoryList = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const HistoryPill = styled.div`
  padding: 4px 12px;
  border-radius: 20px;
  background: rgba(167,139,250,0.15);
  border: 1px solid rgba(167,139,250,0.3);
  color: #c4b5fd;
  font-size: 13px;
  font-weight: 600;
`;

const ResetBtn = styled.button`
  padding: 10px 24px;
  border-radius: 12px;
  border: 1px solid rgba(239,68,68,0.4);
  background: rgba(239,68,68,0.12);
  color: #fca5a5;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: rgba(239,68,68,0.25); }
`;

// ── CONFETTI PARTICLE ─────────────────────────────────────────────────────────
const ConfettiPiece = styled.div<{ $x: number; $delay: number; $color: string; $size: number }>`
  position: fixed;
  top: -20px;
  left: ${p => p.$x}vw;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  background: ${p => p.$color};
  border-radius: 2px;
  animation: ${confettiFall} ${p => 1.5 + p.$delay}s ease-in ${p => p.$delay}s forwards;
  pointer-events: none;
  z-index: 9999;
`;

// ── COLORS ────────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  'linear-gradient(135deg,#7c3aed,#4f46e5)',
  'linear-gradient(135deg,#0ea5e9,#0284c7)',
  'linear-gradient(135deg,#10b981,#059669)',
  'linear-gradient(135deg,#f59e0b,#d97706)',
  'linear-gradient(135deg,#ef4444,#dc2626)',
  'linear-gradient(135deg,#ec4899,#db2777)',
  'linear-gradient(135deg,#8b5cf6,#7c3aed)',
  'linear-gradient(135deg,#14b8a6,#0d9488)',
];

// ── SPIN WHEEL CANVAS COMPONENT ─────────────────────────────────────────────
const WHEEL_COLORS = [
  '#7c3aed','#0ea5e9','#10b981','#f59e0b',
  '#ef4444','#ec4899','#6366f1','#14b8a6',
  '#f43f5e','#22c55e','#fb923c','#8b5cf6',
];

const SpinWheelCanvas: React.FC<{
  available: Student[];
  spinning: boolean;
  setSpinning: (v: boolean) => void;
  onWinner: (s: Student) => void;
}> = ({ available, spinning, setSpinning, onWinner }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const angleRef = React.useRef(0);
  const rafRef = React.useRef<number>();

  const draw = React.useCallback((angle: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const n = available.length;
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2;
    const r = Math.min(cx, cy) - 32;
    ctx.clearRect(0, 0, W, H);
    if (n === 0) {
      ctx.fillStyle = '#475569'; ctx.font = 'bold 14px Inter,sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('Không còn học sinh', cx, cy); return;
    }
    const seg = (2 * Math.PI) / n;
    for (let i = 0; i < n; i++) {
      const s = angle + i * seg, e = s + seg, mid = (s + e) / 2;
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.arc(cx, cy, r, s, e); ctx.closePath();
      ctx.fillStyle = WHEEL_COLORS[i % WHEEL_COLORS.length]; ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.lineWidth = 2; ctx.stroke();
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(mid);
      ctx.textAlign = 'right'; ctx.fillStyle = 'white';
      ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = 4;
      const fs = Math.max(12, Math.min(22, 260 / n));
      ctx.font = `bold ${fs}px Inter,sans-serif`; ctx.textBaseline = 'middle';
      const nm = available[i].name.length > 13 ? available[i].name.slice(0,12)+'…' : available[i].name;
      ctx.fillText(nm, r * 0.72, 0); ctx.restore();
    }
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2*Math.PI);
    ctx.strokeStyle = 'rgba(255,255,255,0.8)'; ctx.lineWidth = 5; ctx.stroke();
    // Hub
    ctx.beginPath(); ctx.arc(cx, cy, 22, 0, 2*Math.PI);
    const g = ctx.createRadialGradient(cx-4,cy-4,2,cx,cy,22);
    g.addColorStop(0,'#c4b5fd'); g.addColorStop(1,'#4f46e5');
    ctx.fillStyle = g; ctx.fill();
    ctx.strokeStyle = 'white'; ctx.lineWidth = 3; ctx.stroke();
    // Pointer ▼ at top
    const py = cy - r - 6;
    ctx.beginPath(); ctx.moveTo(cx, py+4); ctx.lineTo(cx-15, py-22); ctx.lineTo(cx+15, py-22);
    ctx.closePath(); ctx.fillStyle = '#fbbf24'; ctx.fill();
    ctx.strokeStyle = '#d97706'; ctx.lineWidth = 2; ctx.stroke();
  }, [available]);

  React.useEffect(() => { draw(angleRef.current); }, [draw]);

  const spinWheel = () => {
    if (spinning || available.length === 0) return;
    setSpinning(true);
    const n = available.length;
    const seg = (2 * Math.PI) / n;
    const winnerIdx = Math.floor(Math.random() * n);
    // Pointer is at top = -π/2. Winner midpoint must land there.
    const baseTarget = -Math.PI/2 - winnerIdx * seg - seg/2;
    let target = baseTarget;
    const minSpins = 6 + Math.random() * 4;
    while (target - angleRef.current < minSpins * 2 * Math.PI) target += 2 * Math.PI;
    const startAngle = angleRef.current;
    const startTime = performance.now();
    const duration = 4000 + n * 40 + Math.random() * 1200;
    playWhoosh();
    let lastTick = startAngle;
    const animate = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 5);
      const cur = startAngle + (target - startAngle) * ease;
      angleRef.current = cur; draw(cur);
      if (Math.abs(cur - lastTick) > seg * 0.55) { playTick(); lastTick = cur; }
      if (t < 1) { rafRef.current = requestAnimationFrame(animate); }
      else { angleRef.current = target; draw(target); setSpinning(false); onWinner(available[winnerIdx]); }
    };
    rafRef.current = requestAnimationFrame(animate);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <canvas ref={canvasRef} width={680} height={680}
        style={{ width: '100%', maxWidth: 680, height: 'auto', cursor: spinning ? 'default' : 'pointer',
          filter: spinning ? 'drop-shadow(0 0 32px rgba(167,139,250,0.8))' : 'drop-shadow(0 4px 20px rgba(0,0,0,0.6))' }}
        onClick={spinWheel} />
      <BigPickBtn onClick={spinWheel} disabled={spinning || available.length === 0} $spinning={spinning}
        style={{ maxWidth: 440, marginTop: 16, display: 'block' }}>
        {spinning ? '🌀 Đang quay...' : available.length === 0 ? '🎉 Tất cả đã chọn!' : '🎡 Xoay vòng!'}
      </BigPickBtn>
    </div>
  );
};

type Mode = 'random' | 'spin' | 'card' | 'wheel';

interface Student { id: number; name: string; avatar?: string | null; }

// Avatar lưu dạng path trên server → cần prefix API_URL
function getAvatarUrl(avatar?: string | null): string | undefined {
  if (!avatar) return undefined;
  // Nếu đã là full URL hoặc base64 thì dùng thẳng
  if (avatar.startsWith('http') || avatar.startsWith('data:')) return avatar;
  return `${process.env.REACT_APP_API_URL}${avatar}`;
}

const StudentPickerPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const classes = useAppSelector((s: any) => s.classes.classesList || []);
  const allStudents = useAppSelector((s: any) => s.students.studentsList || []);

  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [mode, setMode] = useState<Mode>('spin');
  const [pickedIds, setPickedIds] = useState<Set<number>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<number>>(new Set()); // Nghỉ / loại khỏi danh sách
  const [editMode, setEditMode] = useState(false);
  const [currentWinner, setCurrentWinner] = useState<Student | null>(null);
  const [pickedHistory, setPickedHistory] = useState<Student[]>([]);
  const [spinning, setSpinning] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [confetti, setConfetti] = useState<any[]>([]);
  const [spinAngle, setSpinAngle] = useState(0);

  // Điểm danh hôm nay
  const [presentStudentIds, setPresentStudentIds] = useState<Set<number> | null>(null); // null = chưa load / không có buổi
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  const tickRef = useRef<any>(null);
  const spinRef = useRef<number>(0);

  useEffect(() => {
    dispatch(classActions.getAll());
    dispatch(studentActions.getAll());
  }, [dispatch]);

  // Khi chọn lớp → check xem hôm nay có buổi điểm danh không
  useEffect(() => {
    if (!selectedClassId) { setPresentStudentIds(null); return; }
    const today = new Date().toISOString().split('T')[0];
    setAttendanceLoading(true);
    attendanceSessionService.getByClassAndDate(Number(selectedClassId), today)
      .then((res: any) => {
        // Lọc những học sinh có mặt (isPresent = true)
        const records: any[] = res.studentRecords || [];
        const presentIds = new Set<number>(
          records.filter((r: any) => r.isPresent).map((r: any) => r.studentId)
        );
        setPresentStudentIds(presentIds.size > 0 ? presentIds : null);
      })
      .catch(() => {
        // 404 = không có buổi hôm nay → hiện tất cả
        setPresentStudentIds(null);
      })
      .finally(() => setAttendanceLoading(false));
  }, [selectedClassId]);

  // Nếu có điểm danh hôm nay → chỉ show HS có mặt; không thì show all
  const students: Student[] = selectedClassId
    ? allStudents.filter((s: any) => {
        if (String(s.classId) !== selectedClassId || s.archivedAt) return false;
        if (presentStudentIds !== null) return presentStudentIds.has(s.id);
        return true;
      })
    : [];

  // Loại học sinh đã bị exclude + đã chọn
  const available = students.filter(s => !pickedIds.has(s.id) && !excludedIds.has(s.id));

  const showConfetti = useCallback(() => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#fbbf24', '#f472b6', '#f87171'];
    const pieces = Array.from({ length: 30 }, (_, i) => ({
      id: i, x: Math.random() * 100,
      delay: Math.random() * 0.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.random() * 10,
    }));
    setConfetti(pieces);
    setTimeout(() => setConfetti([]), 2500);
  }, []);

  const pickWinner = useCallback((winner: Student) => {
    setCurrentWinner(winner);
    setHighlightId(winner.id);
    setPickedIds(prev => new Set([...prev, winner.id]));
    setPickedHistory(prev => [...prev, winner]);
    playPick();
    showConfetti();
    setTimeout(() => setHighlightId(null), 2000);
  }, [showConfetti]);

  // ── MODE: SPIN ─────────────────────────────────────────────────────────────
  const handleSpin = useCallback(() => {
    if (spinning || available.length === 0) return;
    setSpinning(true);
    setCurrentWinner(null);
    playWhoosh();

    let idx = 0;
    let speed = 60;
    let elapsed = 0;
    const total = 2500 + Math.random() * 1000;
    const pool = [...available];

    const tick = () => {
      const candidate = pool[idx % pool.length];
      setHighlightId(candidate.id);
      playTick();
      idx++;
      elapsed += speed;
      speed = Math.min(speed * 1.04, 300);
      if (elapsed < total) {
        tickRef.current = setTimeout(tick, speed);
      } else {
        const winner = pool[idx % pool.length];
        setHighlightId(null);
        setSpinning(false);
        pickWinner(winner);
      }
    };
    tickRef.current = setTimeout(tick, speed);
  }, [spinning, available, pickWinner]);

  // ── MODE: RANDOM FLASH ────────────────────────────────────────────────────
  const handleRandomFlash = useCallback(() => {
    if (spinning || available.length === 0) return;
    setSpinning(true);
    setCurrentWinner(null);
    playWhoosh();

    let count = 0;
    const flashes = 15 + Math.floor(Math.random() * 10);
    const pool = [...available];

    const flash = () => {
      const r = pool[Math.floor(Math.random() * pool.length)];
      setHighlightId(r.id);
      playTick();
      count++;
      const delay = count < flashes ? 80 + count * 15 : 400;
      if (count < flashes) {
        tickRef.current = setTimeout(flash, delay);
      } else {
        const winner = pool[Math.floor(Math.random() * pool.length)];
        setHighlightId(null);
        setSpinning(false);
        pickWinner(winner);
      }
    };
    tickRef.current = setTimeout(flash, 80);
  }, [spinning, available, pickWinner]);

  // ── MODE: CARD ─────────────────────────────────────────────────────────────
  const handleCardPick = useCallback(() => {
    if (available.length === 0) return;
    const winner = available[Math.floor(Math.random() * available.length)];
    pickWinner(winner);
  }, [available, pickWinner]);

  const handlePick = mode === 'spin' ? handleSpin : mode === 'random' ? handleRandomFlash : handleCardPick;

  const handleReset = () => {
    setPickedIds(new Set());
    setPickedHistory([]);
    setCurrentWinner(null);
    setHighlightId(null);
    if (tickRef.current) clearTimeout(tickRef.current);
    setSpinning(false);
    // Giữ excludedIds để không phải chỉnh lại sau reset
  };

  const handleExclude = (id: number) => {
    setExcludedIds(prev => new Set([...prev, id]));
    // Nếu đang là winner thì clear
    if (currentWinner?.id === id) setCurrentWinner(null);
  };

  const handleInclude = (id: number) => {
    setExcludedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  const handleResetAll = () => {
    handleReset();
    setExcludedIds(new Set());
    setEditMode(false);
  };

  const getColor = (id: number) => AVATAR_COLORS[id % AVATAR_COLORS.length];

  const modeLabelMap: Record<Mode, string> = {
    spin: '⚡ Nhấp nháy',
    random: '🃏 Lật thẻ bí ẩn',
    card: '🎴 Chọn ngẫu nhiên',
    wheel: '🎡 Vòng quay',
  };
  const modeLabel = modeLabelMap[mode];
  const btnLabel = spinning ? '✨ Đang chọn...' : available.length === 0 ? '🎉 Tất cả đã được chọn!' : `${modeLabel} — Chọn học sinh!`;
  const activeCount = students.length - excludedIds.size;

  return (
    <Page>
      {/* Confetti */}
      {confetti.map(c => <ConfettiPiece key={c.id} $x={c.x} $delay={c.delay} $color={c.color} $size={c.size} />)}

      <Title>🎯 Chọn Học Sinh</Title>

      {/* Setup */}
      <SetupRow>
        <div style={{ flex: 1 }}>
          <Label>Chọn lớp</Label>
          <SelectBox value={selectedClassId} onChange={e => { setSelectedClassId(e.target.value); handleResetAll(); }}>
            <option value="">-- Chọn lớp --</option>
            {[...classes].sort((a: any, b: any) => a.name.localeCompare(b.name)).map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </SelectBox>
        </div>
        {selectedClassId && students.length > 0 && (
          <EditModeBtn $active={editMode} onClick={() => setEditMode(e => !e)}>
            {editMode ? '✅ Xong chỉnh sửa' : '✏️ Chỉnh danh sách'}
          </EditModeBtn>
        )}
        {pickedHistory.length > 0 && !editMode && (
          <ResetBtn onClick={handleReset}>🔄 Chơi lại</ResetBtn>
        )}
        {excludedIds.size > 0 && !editMode && (
          <ResetBtn onClick={handleResetAll} style={{ borderColor: 'rgba(100,116,139,0.4)', background: 'rgba(100,116,139,0.1)', color: '#94a3b8' }}>↺ Reset tất cả</ResetBtn>
        )}
      </SetupRow>

      {/* Badge trạng thái điểm danh hôm nay */}
      {selectedClassId && (
        <div style={{ maxWidth: 900, margin: '-12px auto 16px', textAlign: 'center' }}>
          {attendanceLoading ? (
            <span style={{ fontSize: 13, color: '#64748b' }}>⏳ Đang kiểm tra điểm danh hôm nay...</span>
          ) : presentStudentIds !== null ? (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)',
              borderRadius: 20, padding: '4px 14px', fontSize: 13, color: '#34d399', fontWeight: 700
            }}>
              📋 Có điểm danh hôm nay — chỉ hiện {presentStudentIds.size} học sinh có mặt
            </span>
          ) : (
            <span style={{ fontSize: 12, color: '#475569' }}>
              ℹ️ Chưa điểm danh hôm nay — hiển thị toàn bộ lớp
            </span>
          )}
        </div>
      )}

      {selectedClassId && students.length > 0 && (
        <>
          {/* Mode selector */}
          <ModeBar>
            {([['spin','⚡ Nhấp nháy'], ['random','🃏 Lật thẻ bí ẩn'], ['card','🎴 Chọn ngẫu nhiên'], ['wheel','🎡 Vòng quay']] as [Mode,string][]).map(([m,label]) => (
              <ModeBtn key={m} $active={mode === m} onClick={() => { setMode(m); setCurrentWinner(null); }}>
                {label}
              </ModeBtn>
            ))}
          </ModeBar>

          <ArenaBox style={mode === 'wheel' ? { maxWidth: '100%' } : undefined}>
            {/* Wheel mode — 2-column layout: wheel left (2/3), winner right (1/3) */}
            {mode === 'wheel' && !editMode && (
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
                {/* LEFT — wheel canvas (2/3) */}
                <div style={{ flex: '0 0 66%', maxWidth: '66%' }}>
                  <SpinWheelCanvas
                    available={available}
                    spinning={spinning}
                    setSpinning={setSpinning}
                    onWinner={pickWinner}
                  />
                </div>

                {/* RIGHT — winner + history (1/3) */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                  {currentWinner ? (
                    <WinnerCard style={{ marginBottom: 0 }}>
                      <WinnerLabel>🌟 Học sinh được chọn 🌟</WinnerLabel>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <AvatarCircle $size={120} $color={getColor(currentWinner.id)} $src={getAvatarUrl(currentWinner.avatar)} $bounce $glow>
                          {getAvatarUrl(currentWinner.avatar)
                            ? <AvatarImg src={getAvatarUrl(currentWinner.avatar)} alt={currentWinner.name} />
                            : currentWinner.name[0]?.toUpperCase()
                          }
                        </AvatarCircle>
                      </div>
                      <WinnerName>{currentWinner.name}</WinnerName>
                    </WinnerCard>
                  ) : (
                    <div style={{
                      background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.1)',
                      borderRadius: 24, padding: 32, textAlign: 'center', color: '#475569', fontSize: 14
                    }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
                      Nhấn <strong style={{ color: '#a78bfa' }}>Xoay vòng</strong><br/>để chọn học sinh
                    </div>
                  )}

                  {/* Picked history */}
                  {pickedHistory.length > 0 && (
                    <HistorySection style={{ margin: 0 }}>
                      <HistoryTitle>Đã chọn ({pickedHistory.length})</HistoryTitle>
                      <HistoryList>
                        {pickedHistory.map((s, i) => (
                          <HistoryPill key={s.id}>#{i + 1} {s.name}</HistoryPill>
                        ))}
                      </HistoryList>
                    </HistorySection>
                  )}

                  {available.length === 0 && pickedHistory.length > 0 && (
                    <div style={{ textAlign: 'center', color: '#a78bfa', fontWeight: 700, fontSize: 16 }}>
                      🎉 Tất cả đã được chọn!
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Other modes — big pick button */}
            {mode !== 'wheel' && !editMode && (
              <BigPickBtn onClick={handlePick} disabled={spinning || available.length === 0} $spinning={spinning}>
                {btnLabel}
              </BigPickBtn>
            )}

            {/* Winner display — chỉ hiện ở các mode không phải wheel (wheel tự hiện bên phải) */}
            {currentWinner && mode !== 'wheel' && (
              <WinnerCard>
                <WinnerLabel>🌟 Học sinh được chọn 🌟</WinnerLabel>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <AvatarCircle $size={120} $color={getColor(currentWinner.id)} $src={getAvatarUrl(currentWinner.avatar)} $bounce $glow>
                    {getAvatarUrl(currentWinner.avatar)
                      ? <AvatarImg src={getAvatarUrl(currentWinner.avatar)} alt={currentWinner.name} />
                      : currentWinner.name[0]?.toUpperCase()
                    }
                  </AvatarCircle>
                </div>
                <WinnerName>{currentWinner.name}</WinnerName>
              </WinnerCard>
            )}

            {/* Student grid */}
            {editMode ? (
              <>
                <div style={{ textAlign: 'center', color: '#fb923c', fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                  ✏️ Chế độ chỉnh sửa — nhấn ✕ để loại học sinh khỏi danh sách chọn
                </div>
                <StudentGrid>
                  {students.map(s => {
                    const isExcluded = excludedIds.has(s.id);
                    return isExcluded ? (
                      <ExcludedCard key={s.id}>
                        <AvatarCircle $size={56} $color={getColor(s.id)} $src={getAvatarUrl(s.avatar)}>
                          {getAvatarUrl(s.avatar) ? <AvatarImg src={getAvatarUrl(s.avatar)} alt={s.name} /> : s.name[0]?.toUpperCase()}
                        </AvatarCircle>
                        <StudentCardName style={{ color: '#94a3b8' }}>{s.name}</StudentCardName>
                        <button onClick={() => handleInclude(s.id)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, border: '1px solid rgba(34,197,94,0.4)', background: 'rgba(34,197,94,0.1)', color: '#86efac', cursor: 'pointer', fontWeight: 700 }}>+ Thêm lại</button>
                      </ExcludedCard>
                    ) : (
                      <StudentCard key={s.id} $picked={false} $highlighted={false} style={{ cursor: 'default' }}>
                        <AvatarCircle $size={56} $color={getColor(s.id)} $src={getAvatarUrl(s.avatar)}>
                          {getAvatarUrl(s.avatar) ? <AvatarImg src={getAvatarUrl(s.avatar)} alt={s.name} /> : s.name[0]?.toUpperCase()}
                        </AvatarCircle>
                        <StudentCardName>{s.name}</StudentCardName>
                        <RemoveBtn onClick={e => { e.stopPropagation(); handleExclude(s.id); }} title="Loại khỏi danh sách">✕</RemoveBtn>
                      </StudentCard>
                    );
                  })}
                </StudentGrid>
                {excludedIds.size > 0 && (
                  <div style={{ textAlign: 'center', color: '#ef4444', fontSize: 13, marginBottom: 16 }}>
                    Đã loại {excludedIds.size} học sinh. Danh sách chơi: <strong style={{ color: '#a78bfa' }}>{activeCount} học sinh</strong>
                  </div>
                )}
              </>
            ) : (
            <StudentGrid>
              {students.filter(s => !excludedIds.has(s.id)).map((s) => {
                const isPicked = pickedIds.has(s.id);
                const isHL = highlightId === s.id;
                return (
                  <StudentCard key={s.id} $picked={isPicked} $highlighted={isHL} data-picked={isPicked ? 'true' : undefined}
                    onClick={() => { if (!isPicked && !spinning) pickWinner(s); }}>
                    <AvatarCircle $size={56} $color={getColor(s.id)} $src={getAvatarUrl(s.avatar)} $bounce={isHL}>
                      {getAvatarUrl(s.avatar) ? <AvatarImg src={getAvatarUrl(s.avatar)} alt={s.name} /> : s.name[0]?.toUpperCase()}
                    </AvatarCircle>
                    <StudentCardName>{s.name}</StudentCardName>
                    {isPicked && <PickedBadge>✅</PickedBadge>}
                  </StudentCard>
                );
              })}
            </StudentGrid>
            )}

            {/* History — ẩn ở wheel mode (đã hiện bên phải) */}
            {pickedHistory.length > 0 && mode !== 'wheel' && (
              <HistorySection>
                <HistoryTitle>Đã chọn ({pickedHistory.length}/{students.length})</HistoryTitle>
                <HistoryList>
                  {pickedHistory.map((s, i) => (
                    <HistoryPill key={s.id}>#{i + 1} {s.name}</HistoryPill>
                  ))}
                </HistoryList>
              </HistorySection>
            )}

            {available.length === 0 && students.length > 0 && !editMode && mode !== 'wheel' && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#a78bfa', fontWeight: 700, fontSize: 18 }}>
                🎉 Tất cả học sinh đã được chọn! Nhấn "Chơi lại" để bắt đầu lại.
              </div>
            )}
          </ArenaBox>
        </>
      )}

      {selectedClassId && students.length === 0 && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '40px', fontSize: 16 }}>
          Lớp này chưa có học sinh nào.
        </div>
      )}

      {!selectedClassId && (
        <div style={{ textAlign: 'center', color: '#64748b', padding: '40px', fontSize: 16 }}>
          👆 Chọn một lớp để bắt đầu!
        </div>
      )}
    </Page>
  );
};

export default StudentPickerPage;
